import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import type {IPlayerProfileOverviewProps} from "./player_profile_overview.component";
import type {IPlayersStore} from "data/stores/players/players.store";
import {IPlayerProfileStats} from "views/components/player_profile_stats/player_profile_stats.component";
import {reduce} from "lodash";
import {ifNoValueShowDash, ifNullShowDash} from "data/utils";
import type {IRoundsStore, ISession} from "data/stores/rounds/rounds.store";

export interface IPlayerProfileOverviewController
	extends ViewController<IPlayerProfileOverviewProps> {
	get i18n(): ILocalizationStore;
	get stats(): IPlayerProfileStats[];
	get rounds(): ISession[];
}

@injectable()
export class PlayerProfileOverviewController implements IPlayerProfileOverviewController {
	@observable private _playerId?: number;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	get player() {
		if (!this._playerId) {
			return null;
		}

		return this._playersStore.getPlayerById(this._playerId);
	}

	get totalPoints() {
		return reduce(this.player?.fantasy, (acc, fantasy) => acc + fantasy.points, 0);
	}

	get firstSession() {
		return this._roundStore.firstSession;
	}

	get recordValue() {
		const stats = this.player?.stats?.seasonRecords;

		if (!stats) {
			return "";
		}

		return `${stats.wins}-${stats.losses}-${stats.draws}`;
	}

	get stats() {
		return [
			{
				label: this.i18n.t("player_profile.avg_points", "Average Pts/Session"),
				shortLabel: this.i18n.t("player_profile.avg_points_short", "Avg Pts/Sess"),
				value: ifNullShowDash(this.player?.stats.avgPoints ?? 0),
			},
			{
				label: this.i18n.t("player_profile.total_points", "Total Pts"),
				value: ifNullShowDash(this.totalPoints),
			},
			{
				label: this.i18n.t("player_profile.record", "Record"),
				value: ifNoValueShowDash(this.recordValue),
			},
		];
	}

	get rounds() {
		return this._roundStore.rounds;
	}

	@action init({playerId}: IPlayerProfileOverviewProps) {
		this._playerId = playerId;
	}
}
